/**
 * Cut lengths for the interior bastupanel.
 *
 * A standing board is cut to the wall's HEIGHT; a lying board is cut to the
 * wall's LENGTH. Same lpm either way (area ÷ cover width does not care which
 * way the boards point) — but a cut list that quotes the wrong dimension sends
 * someone to the saw with the wrong number, which is the whole reason this row
 * has to follow `panelDirection`.
 */
import type { CutRow } from "../../engines/types";
import { DEFAULT_FINISHES, type FinishConfig } from "./types";

export interface InteriorPanelCutInput {
	/** Total panel to buy, lpm — the same figure the material row carries. */
	lmWallPanel: number;
	/**
	 * Shortest floor-to-ceiling run of a standing board, m — the flat wall
	 * height under a mono/gable roof.
	 */
	wallHeightM: number;
	/**
	 * Tallest run, m (ridge on a gable end, high eave on a mono side wall).
	 * Omitted, or equal to `wallHeightM`, means every board is the same length.
	 *
	 * Standing boards under a pitched roof are NOT one length: quoting the flat
	 * height alone leaves every board above the eave short, and the panel
	 * quantity the row is priced from already includes that taller area.
	 */
	maxWallHeightM?: number;
	/** Building width and length, m — the two lying-board run lengths. */
	widthM: number;
	lengthM: number;
	finishes?: FinishConfig;
	/** Suffix for the part label, e.g. `" (bastudel)"` on the two-room engine. */
	partSuffix?: string;
}

const MM = (metres: number) => (metres * 1000).toFixed(0);

/**
 * One cut row for the interior wall panel. Emitted by every rectangular
 * engine so the kapnota can actually build the panel the BOM just bought —
 * before this the wall panel had a material row and no cut row at all.
 */
export function buildInteriorPanelCutRow(input: InteriorPanelCutInput): CutRow {
	const finishes = input.finishes ?? DEFAULT_FINISHES;
	const suffix = input.partSuffix ?? "";
	const lm = Math.max(0, input.lmWallPanel);

	if (finishes.panelDirection === "lying") {
		// Boards run along the wall: front/back are cut to W, sides to L. The
		// board count uses the mean of the four runs — the two lengths are
		// spelled out in `cutLength`, so the count is a purchasing aid, not the
		// per-wall truth.
		const meanRunM = (2 * input.widthM + 2 * input.lengthM) / 4;
		return {
			part: `Bastupanel liggande${suffix}`,
			count: `${Math.ceil(lm / Math.max(meanRunM, 0.1))} st`,
			cutLength: `Fram/bak ${MM(input.widthM)} mm, sidor ${MM(input.lengthM)} mm`,
			materialKey: "interior.sauna-panel",
			purchaseDimension: "15x90",
		};
	}

	const minH = Math.max(input.wallHeightM, 0.1);
	const maxH = Math.max(input.maxWallHeightM ?? minH, minH);
	// Boards are bought at the LONGEST run and cut down — buying at the flat
	// height and trimming up is not a thing you can do.
	const boardCount = Math.ceil(lm / maxH);
	const sloped = maxH - minH > 0.01;

	return {
		part: `Bastupanel stående${suffix}`,
		count: `${boardCount} st`,
		cutLength: sloped
			? // Reuses the two phrases `translateCutLength` already maps
				// ("Kapas på plats", "mot taklutning") rather than inventing a
				// third spelling that would leak Swedish to intl.
				`${MM(minH)}–${MM(maxH)} mm, Kapas på plats mot taklutning`
			: `${MM(minH)} mm`,
		materialKey: "interior.sauna-panel",
		purchaseDimension: "15x90",
	};
}
